import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Plus, Trash2, ShieldAlert } from 'lucide-react';
import type { StageBlocker } from '@/types/pipeline';

interface StageBlockersProps {
  blockersEnabled: boolean;
  onBlockersEnabledChange: (enabled: boolean) => void;
  blockers: StageBlocker[];
  onBlockersChange: (blockers: StageBlocker[]) => void;
}

const PROJECT_FIELDS = [
  // ⚠️ F-114 (JCO-2.2). `system_cost` is REVENUE: `project_revenue()` reads it as revenue and `project_margin()` computes margin as `system_cost - direct_cost`. Labelled to match `MARGIN_LABELS.revenue`, so this figure cannot sit beside the margin panel's "Direct cost" under the same word.
  { value: 'system_cost', label: 'Contract Value' },
  { value: 'estimated_system_size', label: 'System Size' },
  { value: 'financing_option', label: 'Financing Option' },
  { value: 'module_manufacturer', label: 'Module Type' },
  { value: 'inverter_type', label: 'Inverter Type' },
  { value: 'contract_signed_date', label: 'Contract Signed Date' },
  { value: 'ntp_approved_date', label: 'NTP Approved Date' },
  { value: 'company_name', label: 'Company Name' },
  { value: 'contact_email', label: 'Contact Email' },
  { value: 'contact_phone', label: 'Contact Phone' },
  { value: 'project_street', label: 'Project Address' },
  { value: 'project_city', label: 'City' },
  { value: 'project_state', label: 'State' },
  { value: 'project_zip_code', label: 'ZIP Code' },
];

export function StageBlockers({
  blockersEnabled,
  onBlockersEnabledChange,
  blockers,
  onBlockersChange,
}: StageBlockersProps) {
  const handleAddBlocker = () => {
    const newBlocker: StageBlocker = {
      id: `blocker-${Date.now()}`,
      field_path: '',
      field_label: '',
      condition: 'not_empty',
      source: 'project',
    };
    onBlockersChange([...blockers, newBlocker]);
  };

  const handleUpdateBlocker = (index: number, updates: Partial<StageBlocker>) => {
    const updated = [...blockers];
    updated[index] = { ...updated[index], ...updates };

    // Auto-set field_label when field_path changes
    if (updates.field_path) {
      const field = PROJECT_FIELDS.find(f => f.value === updates.field_path);
      if (field) {
        updated[index].field_label = field.label;
      }
    }

    onBlockersChange(updated);
  };

  const handleRemoveBlocker = (index: number) => {
    onBlockersChange(blockers.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ShieldAlert className="h-4 w-4 text-amber-500" />
          <Label className="text-sm font-semibold">Stage Blockers</Label>
        </div>
        <Switch
          checked={blockersEnabled}
          onCheckedChange={onBlockersEnabledChange}
        />
      </div>

      {blockersEnabled && (
        <div className="space-y-3">
          <p className="text-xs text-muted-foreground">
            Projects cannot advance to this stage until all conditions are met.
            Backward movement is never blocked.
          </p>

          {blockers.map((blocker, index) => (
            <Card key={blocker.id} className="border-amber-200">
              <CardContent className="p-3 space-y-2">
                <div className="flex items-center gap-2">
                  <Select
                    value={blocker.field_path}
                    onValueChange={(value) => handleUpdateBlocker(index, { field_path: value })}
                  >
                    <SelectTrigger className="flex-1 h-8 text-sm">
                      <SelectValue placeholder="Select field..." />
                    </SelectTrigger>
                    <SelectContent>
                      {PROJECT_FIELDS.map(field => (
                        <SelectItem key={field.value} value={field.value}>
                          {field.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <Select
                    value={blocker.condition}
                    onValueChange={(value) => handleUpdateBlocker(index, { condition: value as StageBlocker['condition'] })}
                  >
                    <SelectTrigger className="w-36 h-8 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="not_empty">Is not empty</SelectItem>
                      <SelectItem value="completed">Is completed</SelectItem>
                      <SelectItem value="equals">Equals</SelectItem>
                    </SelectContent>
                  </Select>

                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0 text-destructive"
                    onClick={() => handleRemoveBlocker(index)}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>

                {blocker.condition === 'equals' && (
                  <Input
                    value={blocker.value || ''}
                    onChange={(e) => handleUpdateBlocker(index, { value: e.target.value })}
                    placeholder="Required value..."
                    className="h-8 text-sm"
                  />
                )}
              </CardContent>
            </Card>
          ))}

          <Button
            type="button"
            variant="outline"
            size="sm"
            className="w-full"
            onClick={handleAddBlocker}
          >
            <Plus className="h-3.5 w-3.5 mr-1" />
            Add Blocker Condition
          </Button>
        </div>
      )}
    </div>
  );
}
